import { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import type { Json, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { invalidateBriefingCaches } from "@/lib/invalidateBriefingCaches";
import { safeLocalStorage } from "@/lib/safeStorage";
import {
  SaveBlockedError,
  breakerForProduction,
  isBriefingSaveConflict,
  resetBreakerForProduction,
  saveBlockedMessage,
} from "@/lib/saveConflictBreaker";

const STORAGE_KEY = "produce.currentProductionId";
const AUTO_SAVE_DELAY = 800; // ms

// Productions whose breaker we have already reported. A tripped
// breaker means the caller is looping, so without this the report
// itself would be the next flood.
const reportedTrips = new Set<string>();

/**
 * A tripped breaker is a bug, not a user error: the save never
 * converged. Report it once so the next occurrence names the
 * production and the browser instead of only showing up as a database
 * CPU alert hours later — which is how the 2026-08-05 incident was
 * found.
 */
function reportTrippedOnce(productionId: string, conflictStreak: number) {
  if (reportedTrips.has(productionId)) return;
  reportedTrips.add(productionId);
  Sentry.captureMessage("save_briefing_core: conflict breaker tripped", {
    level: "error",
    tags: { feature: "production-save" },
    extra: { productionId, conflictStreak },
  });
}

interface ProductionState {
  id: string | null;
  updatedAt: string | null;
}

export const useProductionState = (initialId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const [productionId, setProductionId] = useState<string | null>(() => {
    // Initialize state based on initialId
    // If there's no initialId (new production), clear localStorage immediately
    if (!initialId) {
      safeLocalStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return initialId;
  });
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveInProgressRef = useRef(false);

  // Sync with initialId changes (for when editing existing production)
  useEffect(() => {
    if (initialId) {
      setProductionId(initialId);
    }
  }, [initialId]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (productionId) {
      const state: ProductionState = { id: productionId, updatedAt };
      safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [productionId, updatedAt]);

  // Clear production state
  const clearProductionState = useCallback(() => {
    // The producer is done with this production; forget its conflict
    // history so a later visit starts clean.
    resetBreakerForProduction(productionId);
    setProductionId(null);
    setUpdatedAt(null);
    safeLocalStorage.removeItem(STORAGE_KEY);
  }, [productionId]);

  // Create new production (POST) - only called in Step 1
  const createProduction = useCallback(
    async (data: Partial<TablesInsert<"briefings">>) => {
      if (saveInProgressRef.current) {
        return null;
      }

      try {
        setIsSaving(true);
        saveInProgressRef.current = true;

        const { data: newProduction, error } = await supabase
          .from("briefings")
          .insert({
            ...data,
            status: "draft",
            project_id: "00000000-0000-0000-0000-000000000000",
            producer_id: userId,
          })
          .select()
          .single();

        if (error) throw error;

        setProductionId(newProduction.id);
        setUpdatedAt(newProduction.updated_at);

        // Update URL without navigation
        window.history.replaceState({}, "", `/briefings/${newProduction.id}/edit`);

        queryClient.invalidateQueries({ queryKey: ["briefings"] });

        return newProduction;
      } catch (error: unknown) {
        console.error(error);
        toast({
          title: "Error creating production",
          description: error instanceof Error ? error.message : "Failed to create production",
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsSaving(false);
        saveInProgressRef.current = false;
      }
    },
    [toast, queryClient, userId],
  );

  // Update production (PATCH) - used from Step 2 onwards
  const updateProduction = useCallback(
    async (data: Partial<TablesUpdate<"briefings">>, options?: { silent?: boolean }) => {
      if (!productionId) {
        return null;
      }

      if (saveInProgressRef.current) {
        return null;
      }

      // Brake in front of the only path to save_briefing_core. See
      // saveConflictBreaker.ts: an unbounded retry on the concurrency
      // conflict burned 80% of production's database CPU on 2026-08-05.
      // Whatever is calling — a retry loop, a re-rendering effect, a
      // second tab — it stops here rather than at the database.
      //
      // Throws rather than returning null: the two `return null`s above
      // mean "nothing to do", and the caller treats them as harmless.
      // This one means the briefing fields were NOT written, and must
      // abort the whole save.
      const breaker = breakerForProduction(productionId);
      const decision = breaker.attempt();
      if (!decision.allowed) {
        if (decision.reason === "tripped") {
          reportTrippedOnce(productionId, breaker.conflictStreak());
        }
        throw new SaveBlockedError(decision.reason, saveBlockedMessage(decision));
      }

      try {
        setIsSaving(true);
        saveInProgressRef.current = true;

        // Verify ownership before updating
        if (!userId) {
          throw new Error("User not authenticated");
        }

        // Single RPC replaces the previous fan-out (ownership SELECT
        // + concurrency SELECT + briefings UPDATE + RETURNING). Uses
        // PATCH semantics — only fields supplied in `data` are
        // touched. Optimistic concurrency enforced by passing the
        // local updatedAt as p_expected_updated_at; a mismatch is a
        // conflict (see isBriefingSaveConflict) we surface as a toast.
        const rpcArgs: { p_briefing_id: string; p_data: Json; p_expected_updated_at?: string } = {
          p_briefing_id: productionId,
          p_data: data as Json,
        };
        if (updatedAt) rpcArgs.p_expected_updated_at = updatedAt;
        const { data: updated, error } = await supabase.rpc("save_briefing_core", rpcArgs);

        if (error) {
          // Optimistic concurrency mismatch (see isBriefingSaveConflict).
          // Refresh the local timestamp so the next save proceeds.
          if (isBriefingSaveConflict(error)) {
            breaker.recordConflict();
            const { data: current } = await supabase
              .from("briefings")
              .select("updated_at")
              .eq("id", productionId)
              .single();
            if (current) setUpdatedAt(current.updated_at);
            if (!options?.silent) {
              toast({
                title: "Conflict detected",
                description: "Production was updated elsewhere. Please retry.",
                variant: "default",
              });
            }
          }
          throw error;
        }

        breaker.recordSuccess();
        setUpdatedAt(updated.updated_at);

        if (!options?.silent && productionId) {
          await invalidateBriefingCaches(queryClient, productionId);
        }

        return updated;
      } catch (error: unknown) {
        console.error(error);
        if (!options?.silent) {
          toast({
            title: "Error saving changes",
            description: error instanceof Error ? error.message : "Failed to save changes",
            variant: "destructive",
          });
        }
        throw error;
      } finally {
        setIsSaving(false);
        saveInProgressRef.current = false;
      }
    },
    [productionId, updatedAt, toast, queryClient, userId],
  );

  // Debounced auto-save
  const scheduleAutoSave = useCallback(
    (data: Partial<TablesUpdate<"briefings">>) => {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Schedule new auto-save
      autoSaveTimeoutRef.current = setTimeout(async () => {
        if (!productionId) {
          return;
        }

        try {
          await updateProduction(data, { silent: true });
        } catch (error) {
          console.error(error);
        }
      }, AUTO_SAVE_DELAY);
    },
    [productionId, updateProduction],
  );

  // Cancel pending auto-save
  const cancelAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  // Manual save (used by Next/Back buttons)
  const saveProduction = useCallback(
    async (data: Partial<TablesInsert<"briefings">>, status: "draft" | "published" = "draft") => {
      cancelAutoSave(); // Cancel any pending auto-save

      const dataWithStatus = { ...data, status };

      if (!productionId) {
        // First save - create production
        return await createProduction(dataWithStatus);
      } else {
        // Subsequent saves - update production
        return await updateProduction(dataWithStatus);
      }
    },
    [productionId, createProduction, updateProduction, cancelAutoSave],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAutoSave();
    };
  }, [cancelAutoSave]);

  return {
    productionId,
    updatedAt,
    isSaving,
    createProduction,
    updateProduction,
    saveProduction,
    scheduleAutoSave,
    cancelAutoSave,
    clearProductionState,
  };
};
